/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1672790400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jan 04 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "calixa",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Supported types",
          "depth": 0,
          "id": "supported-types"
        }, {
          "value": "Syncing objects",
          "depth": 0,
          "id": "syncing-objects"
        }, {
          "value": "Object types",
          "depth": 1,
          "id": "object-types"
        }, {
          "value": "Event types",
          "depth": 1,
          "id": "event-types"
        }, {
          "value": "Object sync modes",
          "depth": 1,
          "id": "object-sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      ul: "ul",
      li: "li",
      h3: "h3",
      img: "img"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["To set up Calixa, you will need your Calixa ", _jsx(_components.strong, {
          children: "Account ID"
        }), " and ", _jsx(_components.strong, {
          children: "Auth Token"
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["These can be found in Calixa by clicking the dropdown menu on the Calixa console home page and select ", _jsx(_components.strong, {
          children: "API Keys"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "supported-types",
        children: "Supported types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Calixa types:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Events"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Objects"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-objects",
        children: "Syncing objects"
      }), "\n", _jsx(_components.h3, {
        id: "object-types",
        children: "Object types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Calixa objects:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Users"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Accounts"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "event-types",
        children: "Event types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Calixa events:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Custom User Events"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Custom Account Events"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Published Metric Events"
          })
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h3, {
        id: "object-sync-modes",
        children: "Object sync modes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), " - Upsert mode is supported for Users and Accounts. In upsert mode, Hightouch adds new rows and keeps existing rows up to date."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Records can be matched from your source to your Calixa workspace by an external ID."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-calixa-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Calixa's default and custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-calixa-field-mapping.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
